/**
 * globalSettingsService.js
 * ─────────────────────────
 * Frontend service for the /api/settings backend endpoints.
 *
 * localStorage keys used:
 *   ofl_ai_global_settings  — AI generation settings + banner display fields
 *   em_platform_banner      — full banner config (shared with usePlatformBanner)
 *   ofl_admin_token         — admin token for write operations
 */

const API_BASE = import.meta.env.VITE_SETTINGS_API_URL || 'http://localhost:4000/api/settings';

// ── Defaults (mirrors server) ─────────────────────────────────────────────────
/**
 * AI_SETTINGS_DEFAULTS now includes the three banner display fields so
 * GlobalSettingsPanel can manage them as part of the unified settings state.
 * The master `platform_maintenance` toggle stays in MaintenanceSection / usePlatformBanner.
 */
export const AI_SETTINGS_DEFAULTS = {
  // ── AI generation ──────────────────────────────────────────────────────────
  max_tokens:        4096,
  temperature:       0.3,
  timeout_sec:       60,
  retry_count:       3,
  content_filter:    true,
  audit_logging:     true,
  fallback_enabled:  true,
  pii_masking:       true,
  rate_limit_strict: false,

  // ── Banner display fields (editable in GlobalSettingsPanel) ────────────────
  bannerText:  'We are currently performing scheduled maintenance. Some features may be temporarily unavailable.',
  bannerColor: '#1a2744',
  bannerTheme: 'warning',
};

export const BANNER_DEFAULTS = {
  platform_maintenance: false,
  bannerText:   AI_SETTINGS_DEFAULTS.bannerText,
  bannerColor:  AI_SETTINGS_DEFAULTS.bannerColor,
  bannerTheme:  AI_SETTINGS_DEFAULTS.bannerTheme,
  dismissible:  true,
  showIcon:     true,
  startAt:      null,
  endAt:        null,
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function getAdminToken() {
  return localStorage.getItem('ofl_admin_token') || '';
}

function adminHeaders() {
  return {
    'Content-Type':  'application/json',
    'X-Admin-Token': getAdminToken(),
  };
}

// ── localStorage load helpers ─────────────────────────────────────────────────

/**
 * Load AI settings (+ banner display fields) from localStorage, merged over defaults.
 * Falls back cleanly if JSON is corrupt.
 */
export function loadAiSettingsLocal() {
  try {
    const storedAi     = JSON.parse(localStorage.getItem('ofl_ai_global_settings') || '{}');
    // Also pull banner display fields from the banner key so they stay in sync
    const storedBanner = JSON.parse(localStorage.getItem('em_platform_banner') || '{}');
    return {
      ...AI_SETTINGS_DEFAULTS,
      // Banner display fields from the banner store take precedence (source of truth)
      bannerText:  storedBanner.bannerText  ?? AI_SETTINGS_DEFAULTS.bannerText,
      bannerColor: storedBanner.bannerColor ?? AI_SETTINGS_DEFAULTS.bannerColor,
      bannerTheme: storedBanner.bannerTheme ?? AI_SETTINGS_DEFAULTS.bannerTheme,
      // AI-specific stored values override defaults last
      ...storedAi,
    };
  } catch {
    return { ...AI_SETTINGS_DEFAULTS };
  }
}

/**
 * Load banner config from localStorage, merged over defaults.
 * Uses the same key as usePlatformBanner ('em_platform_banner').
 */
export function loadBannerLocal() {
  try {
    const stored = JSON.parse(localStorage.getItem('em_platform_banner') || '{}');
    return { ...BANNER_DEFAULTS, ...stored };
  } catch {
    return { ...BANNER_DEFAULTS };
  }
}

// ── Backend API calls ─────────────────────────────────────────────────────────

/**
 * Fetch the full settings object from the backend.
 * Returns { ai, banner, updatedAt } or null on failure.
 */
export async function fetchSettings() {
  try {
    const res  = await fetch(`${API_BASE}`, { method: 'GET' });
    const data = await res.json();
    if (data.success && data.data) return data.data;
    return null;
  } catch (err) {
    console.warn('[Settings] fetchSettings failed (server may be offline):', err.message);
    return null;
  }
}

/**
 * Persist AI settings to both localStorage AND the backend API.
 * If the settings object contains banner display fields (bannerText, bannerColor,
 * bannerTheme), they are automatically mirrored into the banner store so both
 * stores stay consistent.
 *
 * Returns { success, error? }.
 */
export async function saveAiSettings(settings) {
  // ── Extract banner display fields and mirror them into em_platform_banner ──
  const { bannerText, bannerColor, bannerTheme, ...aiOnly } = settings;
  const bannerPatch = { bannerText, bannerColor, bannerTheme };

  // Persist full settings (including banner display fields) under AI key
  localStorage.setItem('ofl_ai_global_settings', JSON.stringify(settings));
  window.dispatchEvent(new CustomEvent('ai_settings_updated', { detail: settings }));

  // Mirror banner display fields into the banner store so usePlatformBanner
  // and MaintenanceBanner always render the latest values
  const currentBanner = loadBannerLocal();
  const updatedBanner = { ...currentBanner, ...bannerPatch };
  localStorage.setItem('em_platform_banner', JSON.stringify(updatedBanner));
  window.dispatchEvent(new CustomEvent('platform_banner_updated', { detail: updatedBanner }));

  // ── Backend: save AI settings ──────────────────────────────────────────────
  let aiResult = { success: true };
  try {
    const res  = await fetch(`${API_BASE}/ai`, {
      method:  'PUT',
      headers: adminHeaders(),
      body:    JSON.stringify(aiOnly),
    });
    const data = await res.json();
    if (!res.ok) {
      console.warn('[Settings] AI backend save failed:', data.errors || data.error);
      aiResult = { success: false, error: data.errors?.join(', ') || data.error || 'AI backend save failed.', local: true };
    }
  } catch (err) {
    console.warn('[Settings] AI backend unreachable — saved locally only:', err.message);
    aiResult = { success: true, local: true, warning: 'Saved locally. Backend unreachable.' };
  }

  // ── Backend: mirror banner display fields to /api/settings/banner ─────────
  try {
    await fetch(`${API_BASE}/banner`, {
      method:  'PUT',
      headers: adminHeaders(),
      body:    JSON.stringify(bannerPatch),
    });
  } catch {
    // Silently ignore — AI save result is the primary feedback signal
  }

  return aiResult;
}

/**
 * Persist banner config to both localStorage AND the backend API.
 * Also fires the 'platform_banner_updated' CustomEvent so MaintenanceBanner
 * and usePlatformBanner consumers update immediately without a reload.
 *
 * Returns { success, error? }.
 */
export async function saveBannerSettings(banner) {
  // Always persist locally (shared with usePlatformBanner)
  localStorage.setItem('em_platform_banner', JSON.stringify(banner));
  window.dispatchEvent(new CustomEvent('platform_banner_updated', { detail: banner }));

  // Also keep the AI settings store's banner display fields in sync
  try {
    const storedAi = JSON.parse(localStorage.getItem('ofl_ai_global_settings') || '{}');
    const synced = {
      ...storedAi,
      bannerText:  banner.bannerText  ?? storedAi.bannerText,
      bannerColor: banner.bannerColor ?? storedAi.bannerColor,
      bannerTheme: banner.bannerTheme ?? storedAi.bannerTheme,
    };
    localStorage.setItem('ofl_ai_global_settings', JSON.stringify(synced));
    window.dispatchEvent(new CustomEvent('ai_settings_updated', { detail: synced }));
  } catch { /* non-critical */ }

  try {
    const res  = await fetch(`${API_BASE}/banner`, {
      method:  'PUT',
      headers: adminHeaders(),
      body:    JSON.stringify(banner),
    });
    const data = await res.json();
    if (!res.ok) {
      console.warn('[Settings] Banner backend save failed:', data.errors || data.error);
      return { success: false, error: data.errors?.join(', ') || data.error || 'Backend save failed.', local: true };
    }
    return { success: true, data: data.data };
  } catch (err) {
    console.warn('[Settings] Banner backend unreachable — saved locally only:', err.message);
    return { success: true, local: true, warning: 'Saved locally. Backend unreachable.' };
  }
}

/**
 * Convenience: save both AI settings and banner in one PUT /api/settings call.
 */
export async function saveAllSettings({ ai, banner }) {
  if (ai)     { localStorage.setItem('ofl_ai_global_settings', JSON.stringify(ai)); }
  if (banner) {
    localStorage.setItem('em_platform_banner', JSON.stringify(banner));
    window.dispatchEvent(new CustomEvent('platform_banner_updated', { detail: banner }));
  }

  try {
    const res  = await fetch(`${API_BASE}`, {
      method:  'PUT',
      headers: adminHeaders(),
      body:    JSON.stringify({ ai, banner }),
    });
    const data = await res.json();
    if (!res.ok) {
      return { success: false, error: data.errors?.join(', ') || data.error || 'Backend save failed.', local: true };
    }
    return { success: true, data: data.data };
  } catch (err) {
    console.warn('[Settings] Backend unreachable — saved locally only:', err.message);
    return { success: true, local: true, warning: 'Saved locally. Backend unreachable.' };
  }
}